import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";
import { todayInBangkok } from "../../utils/date";
import MonthSummaryCards from "./MonthSummaryCards";
import ExpenseMonthPicker, { parseMonthParam } from "./ExpenseMonthPicker";
import MonthTable from "./MonthTable";
import SkeletonCard from "./SkeletonCard";
import StateCard from "./StateCard";
import {
  API_MONTH_FORMAT,
  DAILY_EXPENSE_MENU_TITLE,
  MONTH_PARAM,
  UNIT_TEXT,
} from "./constants";
import {
  MonthFilter,
  filterMonthRows,
  monthListTitleOf,
  toggleMonthFilter,
} from "./dailyExpenseMonth";
import { formatMonthLabel } from "./format";
import { useDailyExpenseMonth } from "./useDailyExpenseData";

/**
 * S2 ค่าใช้จ่ายรายวัน, the list: every day of the month the branch ran (or wrote a document), with
 * its status and totals. The month lives in the query string, so ย้อนกลับ from a day lands here
 * on the same month; the summary boxes filter the rows to the days still asking for work.
 */
export default function DailyExpenseMonthList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get(MONTH_PARAM);
  const month = useMemo(
    () => parseMonthParam(monthParam) ?? todayInBangkok().startOf("month"),
    [monthParam],
  );
  const monthKey = month.format(API_MONTH_FORMAT);
  const { data, isLoading, isFailed, reload } = useDailyExpenseMonth(monthKey);
  const [filter, setFilter] = useState<MonthFilter>("ALL");

  const handleChangeMonth = (value: Dayjs | null) => {
    // A half-typed month is not a month yet; the list keeps the last whole one.
    if (!value?.isValid()) return;
    setFilter("ALL");
    setSearchParams({ [MONTH_PARAM]: value.format(API_MONTH_FORMAT) }, { replace: true });
  };
  const handleSelectFilter = (clicked: MonthFilter) =>
    setFilter((current) => toggleMonthFilter(current, clicked));

  const rows = data ? filterMonthRows(data.rows, filter) : [];
  const isEmpty = data !== null && data.rows.length === 0;

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>
          {DAILY_EXPENSE_MENU_TITLE}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body2">เดือน</Typography>
<ExpenseMonthPicker value={month} onChange={handleChangeMonth} />
        </Stack>
      </Stack>

      {data?.is_month_closed && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: "gray.main", color: "common.white" }}
        >
          <LockIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            ปิดเดือนแล้ว แก้ไขไม่ได้
          </Typography>
        </Stack>
      )}

      {isLoading && <SkeletonCard />}
      {isFailed && (
        <StateCard
          title="โหลดรายการค่าใช้จ่ายไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่อีกครั้ง"
          onRetry={reload}
        />
      )}
      {isEmpty && (
        <StateCard
          title="ยังไม่มีวันที่สาขาวิ่งในเดือนนี้"
          text="รายการจะขึ้นอัตโนมัติเมื่อมีการวิ่งสายและแจ้งยอดโอน"
        />
      )}
      {data && !isEmpty && (
        <>
          <MonthSummaryCards
            totalCount={data.rows.length}
            notStartedCount={data.not_started_count}
            rejectedCount={data.rejected_count}
            filter={filter}
            onSelect={handleSelectFilter}
          />
          <Paper>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography fontWeight={600}>
                {monthListTitleOf(filter, formatMonthLabel(monthKey))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {UNIT_TEXT}
              </Typography>
            </Stack>
            <MonthTable rows={rows} isMonthClosed={data.is_month_closed} />
          </Paper>
        </>
      )}
    </Stack>
  );
}
